import { Component, OnInit, AfterViewInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { RoutePartsService } from './shared/services/route-parts.service';
import { catchError, filter, map } from 'rxjs/operators';
import { UILibIconService } from './shared/services/ui-lib-icon.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Observable, of } from 'rxjs';
import { environment } from 'environments/environment.prod';
import type { UserInfoResponse } from '@logto/js';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { UserScopeService } from './shared/services/user-scope.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, AfterViewInit {
  appTitle = 'DMS | HANUMAN';
  pageTitle = 'DMS | HANUMAN';
  idleState = 'Not started.';
  timedOut = false;
  lastPing?: Date = null;
  userData?: UserInfoResponse;
  idToken?: string;
  accessToken?: string;
  baseurl = environment.apiURL;

  constructor(
    public title: Title,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private routePartsService: RoutePartsService,
    private iconService: UILibIconService,
    private idle: Idle,
    private keepalive: Keepalive,
    private http: HttpClient,
    private userScopeService: UserScopeService,
  ) {
    iconService.init();
    idle.setIdle(600);
    //set time out period
    idle.setTimeout(5);
    //set default interrupt
    idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
    idle.onTimeout.subscribe(() => {});
    idle.onIdleStart.subscribe(() => (this.idleState = "You've gone idle!"));
    idle.onTimeoutWarning.subscribe(
      (countdown) =>
        (this.idleState = 'You will time out in ' + countdown + ' seconds!'),
    );

    // sets the ping interval to 15 seconds
    this.keepalive.interval(15);

    this.keepalive.onPing.subscribe(() => (this.lastPing = new Date()));

    this.reset();
  }

  ngOnInit() {
    this.changePageTitle();
  }

  getBpIdFromUserId(id: string, token: string): Observable<string | null> {
    return this.http
      .get(
        `${this.baseurl}v1/odata/BusinessPartnerUser?$filter=userId eq ${id}`,
        {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
          }),
        },
      )
      .pipe(
        map((response) => {
          if (
            !Array.isArray(response['value']) ||
            response['value'].length === 0
          ) {
            console.warn('No business partner found for user ID:', id);
            return null;
          }
          return response['value'][0]?.bpId || null;
        }),
        catchError((error) => {
          console.error('Error fetching business partner ID:', error);
          return of(null);
        }),
      );
  }

  /**
   * Initialize user permissions using Casbin
   */
  private initializeUserPermissions(
    organizationId: string | null,
    userId: string,
  ): void {
    // TODO: Extract organization ID from user context if available
    this.userScopeService.loadUserScopes(organizationId, userId).subscribe({
      next: (scopes) => {},
      error: (error) => {
        console.error('❌ Failed to load user scopes:', error);
        // Could implement fallback or default permissions here
      },
    });
  }

  ngAfterViewInit() {}

  changePageTitle() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((routeChange) => {
        const routeParts = this.routePartsService.generateRouteParts(
          this.activeRoute.snapshot,
        );
        if (!routeParts.length) {
          return this.title.setTitle(this.appTitle);
        }
        // Extract title from parts;
        this.pageTitle = routeParts
          .reverse()
          .map((part) => part.title)
          .reduce((partA, partI) => {
            return `${partA} > ${partI}`;
          });
        this.pageTitle += ` | ${this.appTitle}`;
        this.title.setTitle(this.pageTitle);
      });
  }
  reset() {
    this.idle.watch();
    this.idleState = 'Started.';
    this.timedOut = false;
  }
}
